import { Attachment, RaisedButton, useSnackbar, AttachmentType, Dialog, imageTypes } from '@eduact/ed-components'
import { uploadFileWithProgress } from '@Features/Base/BaseApi/BaseApi'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import { ANSWER_ATTACHMENT_ACCEPT, isAllowedAnswerFile, parseAttachmentAnswer } from '@Components/VendoredExam/attachmentAnswer'
import { AxiosProgressEvent } from 'axios'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { PuffLoader } from 'react-spinners'
import { rgba } from 'polished'
import { FlexLayout } from '@eduact/ed-system'

type AttachmentData = {
	url: string
	type: AttachmentType
}
type Props = {
	onChange: (value: string) => void
	value: string
	isEdit?: boolean
}
const AttachmentAnswer: React.VoidFunctionComponent<Props> = ({ onChange, value, isEdit = true }) => {
	const inputRef = useRef<HTMLInputElement | null>(null)
	const { t } = useTranslation('assignment')

	const { openSnackbar } = useSnackbar()
	const handleOnClick = () => inputRef.current?.click()

	// Files still uploading. Identified by the File object, not by index: two
	// concurrent uploads finishing out of order shifted the indices and the
	// wrong entry was removed.
	const [files, setFiles] = useState<Array<File>>([])
	const answer = useMemo<Array<AttachmentData>>(
		() =>
			parseAttachmentAnswer(value)
				.filter(attachment => /^https:\/\//i.test(attachment))
				.map<AttachmentData>(attachment => ({ url: attachment, type: (attachment.split('?')[0].split('.').pop() ?? '') as AttachmentType })),
		[value]
	)
	const onFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected) return
		// Exactly the API's upload allowlist — the picker used to accept
		// image/* and the server rejected svg/heic/bmp after a full upload.
		for (let index = 0; index < selected.length; index++) {
			const file = selected.item(index)
			if (file && !isAllowedAnswerFile(file)) {
				openSnackbar({ ...SnackbarConfig.disclaimer, text: t('only_images_pdfs') })
				return
			}
		}
		setFiles(previous => [...previous, ...Array.from(selected)])
	}

	const onFileUploadFinish = (uri: string, file: File) => {
		setFiles(previous => previous.filter(_ => _ !== file))
		onChange(JSON.stringify(answer.map(_ => _.url).concat(uri)))
	}
	const onFail = (file: File) => {
		// A new array, or React bails out of the render and the spinner tile
		// stays on screen forever; and say so — nothing else reports it.
		setFiles(previous => previous.filter(_ => _ !== file))
		openSnackbar({ ...SnackbarConfig.disclaimer, text: t('file_upload_failed') })
	}
	const onDelete = (index: number) => {
		const remaining = answer.filter((_, i) => i !== index).map(_ => _.url)
		// '' (not '[]'): the form field is a required string, so an emptied
		// attachment list keeps Submit disabled instead of sending a list the
		// API rejects.
		onChange(remaining.length ? JSON.stringify(remaining) : '')
	}
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [openedAttachment, setOpenedAttachment] = useState<AttachmentData | undefined>()
	const handleAttachmentClick = (attachment: AttachmentData) => {
		if (!imageTypes.includes(attachment.type) && attachment.type !== 'pdf') {
			window.open(attachment.url, '_blank', 'noopener')
			return
		}
		setOpenedAttachment(attachment)
		setIsDialogOpen(true)
	}
	const handleDialogClose = () => {
		setIsDialogOpen(false)
	}
	return (
		<div>
			<Dialog persistent={false} overflow={'auto'} maxWidth={{ sm: '90vw', lg: '80vw' }} open={isDialogOpen} onClose={handleDialogClose}>
				<FlexLayout justifyContent={'center'} alignItems="center" width={'100%'} height="100%">
					{openedAttachment && imageTypes.includes(openedAttachment.type) && <img style={{ width: '100%' }} src={openedAttachment.url} />}
					{openedAttachment && openedAttachment.type === 'pdf' && <iframe width={'100%'} height="100%" src={openedAttachment.url}></iframe>}
				</FlexLayout>
			</Dialog>
			<input
				onClick={(e: React.MouseEvent<HTMLInputElement>) => {
					e.currentTarget.value = ''
				}}
				onChange={onFilesChange}
				ref={inputRef}
				type={'file'}
				multiple
				hidden
				accept={ANSWER_ATTACHMENT_ACCEPT}
			/>
			<FlexLayout gridGap={'1.5rem'} alignItems={'center'}>
				{answer.map((attachment, index) => {
					return (
						<Attachment
							withDelete={isEdit}
							onDelete={() => onDelete(index)}
							key={`${attachment.url}-${index}`}
							onClick={handleAttachmentClick}
							type={attachment.type}
							url={attachment.url}
						/>
					)
				})}
				{files.map((file, index) => {
					return <UploadProgress onFail={onFail} key={`${file.name}-${file.lastModified}-${index}`} onFinish={onFileUploadFinish} file={file} />
				})}
				{isEdit && (
					<RaisedButton
						minWidth={'unset'}
						maxWidth={'2.875rem'}
						onClick={handleOnClick}
						btnShape="square"
						btnSize={'small'}
						outlined
						variant="princetonOrange"
						withRipple
					>
						{t('add')}
					</RaisedButton>
				)}
			</FlexLayout>
		</div>
	)
}

export default AttachmentAnswer

const UploadProgress: React.VoidFunctionComponent<{
	file: File
	onFinish: (uri: string, file: File) => void
	onFail: (file: File) => void
}> = ({ file, onFinish, onFail }) => {
	const [, setProgress] = useState<AxiosProgressEvent | null>(null)

	useEffect(() => {
		const formData = new FormData()
		formData.set('folder', 'answers')
		formData.set('file', file as Blob)

		uploadFileWithProgress({
			formData,
			progressCallback(progress) {
				setProgress(progress)
			},
		})
			.then(value => {
				setProgress(null)
				onFinish(value.data.data.uri, file)
			})
			.catch(() => onFail(file))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	return (
		<StyledAttachment>
			<Loader>
				<PuffLoader />
			</Loader>
			<Attachment type={file.name.split('.').pop() as any} url="" folded={false} />
		</StyledAttachment>
	)
}

const Loader = styled.div`
	position: absolute;
	content: '';
	width: 100%;
	height: 100%;
	z-index: 4;
	display: grid;
	place-content: center;
`
const StyledAttachment = styled.div`
	position: relative;
	width: fit-content;
	height: fit-content;
	&::after {
		position: absolute;
		content: '';
		background: ${rgba('#000', 0.3)};
		width: 100%;
		height: 100%;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
`
